package org.wordsapp.model

import kotlinx.serialization.Serializable
import kotlin.math.pow

/**
 * SM-2-style review grade. Binary practice answers map to GOOD/AGAIN;
 * the flashcard review mode exposes all four.
 */
enum class ReviewGrade {
    AGAIN, HARD, GOOD, EASY
}

@Serializable
data class Word(
    val id: Int,
    val foreignWord: String,
    val translation: String,
    val correctAnswers: Int = 0,
    val incorrectAnswers: Int = 0,
    val lastAttemptEpochMs: Long? = null,
    val rowIndex: Int = 0,
    // SM-2 scheduling state. Null for words last saved by an older version of the
    // app — the effective* accessors approximate them from the lifetime counts.
    val streak: Int? = null,
    val easeFactor: Double? = null,
    val intervalDays: Double? = null,
) {
    val totalAttempts: Int get() = correctAnswers + incorrectAnswers

    val successRate: Double
        get() = if (totalAttempts > 0) correctAnswers.toDouble() / totalAttempts else 0.0

    /** Consecutive correct answers since the last failure. */
    val effectiveStreak: Int
        get() = streak ?: (correctAnswers - incorrectAnswers).coerceAtLeast(0)

    val effectiveEase: Double
        get() = easeFactor ?: if (totalAttempts == 0) {
            DEFAULT_EASE
        } else {
            (MIN_EASE + successRate * (DEFAULT_EASE - MIN_EASE)).coerceIn(MIN_EASE, DEFAULT_EASE)
        }

    /** Current inter-review interval; the word is due once this many days pass since the last attempt. */
    val effectiveIntervalDays: Double
        get() = intervalDays ?: when (effectiveStreak) {
            0 -> LEARNING_STEP_DAYS
            1 -> 1.0
            2 -> 6.0
            else -> 6.0 * effectiveEase.pow(effectiveStreak - 2)
        }

    fun daysSinceLastAttempt(nowMs: Long): Double =
        lastAttemptEpochMs
            ?.let { (nowMs - it).coerceAtLeast(0L) / MILLIS_PER_DAY }
            ?: DEFAULT_DAYS_SINCE

    fun isDue(nowMs: Long): Boolean =
        totalAttempts == 0 || daysSinceLastAttempt(nowMs) >= effectiveIntervalDays

    /**
     * SM-2 state transition for one review.
     *
     * - AGAIN: failure — streak resets, ease drops, interval falls back to the learning step.
     * - HARD: success that doesn't advance the ladder — ease drops slightly, interval grows 1.2x.
     * - GOOD: normal success — interval follows 1d / 6d / previous x ease.
     * - EASY: success with an ease boost and a 1.3x interval bonus.
     */
    fun review(grade: ReviewGrade, nowMs: Long): Word {
        val ease = effectiveEase
        val prevInterval = effectiveIntervalDays
        return when (grade) {
            ReviewGrade.AGAIN -> copy(
                incorrectAnswers = incorrectAnswers + 1,
                streak = 0,
                easeFactor = (ease - 0.20).coerceAtLeast(MIN_EASE),
                intervalDays = LEARNING_STEP_DAYS,
                lastAttemptEpochMs = nowMs,
            )
            ReviewGrade.HARD -> copy(
                correctAnswers = correctAnswers + 1,
                streak = effectiveStreak,
                easeFactor = (ease - 0.15).coerceAtLeast(MIN_EASE),
                intervalDays = (prevInterval * 1.2).coerceAtLeast(LEARNING_STEP_DAYS),
                lastAttemptEpochMs = nowMs,
            )
            ReviewGrade.GOOD -> {
                val newStreak = effectiveStreak + 1
                copy(
                    correctAnswers = correctAnswers + 1,
                    streak = newStreak,
                    easeFactor = ease,
                    intervalDays = graduatedInterval(newStreak, ease, prevInterval),
                    lastAttemptEpochMs = nowMs,
                )
            }
            ReviewGrade.EASY -> {
                val newStreak = effectiveStreak + 1
                val newEase = (ease + 0.15).coerceAtMost(MAX_EASE)
                copy(
                    correctAnswers = correctAnswers + 1,
                    streak = newStreak,
                    easeFactor = newEase,
                    intervalDays = graduatedInterval(newStreak, newEase, prevInterval) * EASY_BONUS,
                    lastAttemptEpochMs = nowMs,
                )
            }
        }
    }

    /**
     * Selection weight = [urgency] x [difficultyFactor]. Only ratios matter: selection
     * draws due words strictly before not-yet-due ones and weights compete within a tier.
     */
    fun weight(nowMs: Long): Double = urgency(nowMs) * difficultyFactor

    /**
     * How overdue the word is on its SM-2 schedule: 1 + elapsed/interval, capped at 11.
     * Not-yet-due words fall in 1..2, overdue ones in 2..11; new words get a fixed [NEW_WORD_URGENCY].
     */
    fun urgency(nowMs: Long): Double {
        if (totalAttempts == 0) return NEW_WORD_URGENCY
        val overdueRatio = daysSinceLastAttempt(nowMs) / effectiveIntervalDays
        return 1.0 + overdueRatio.coerceAtMost(10.0)
    }

    /**
     * How much trouble the word gives: 1.0 for a word answered correctly every time,
     * up to ~6 for a word that keeps failing. Failure rate dominates; a long history of
     * lapses adds a little more even when the rate has recovered. New words score 1.0.
     */
    val difficultyFactor: Double
        get() = if (totalAttempts == 0) {
            1.0
        } else {
            1.0 + 4.0 * (1.0 - successRate) + 0.1 * incorrectAnswers.coerceAtMost(10)
        }

    val difficulty: Difficulty
        get() = when {
            totalAttempts == 0 -> Difficulty.NEW
            successRate >= 0.8 -> Difficulty.EASY
            successRate >= 0.5 -> Difficulty.MEDIUM
            else -> Difficulty.HARD
        }

    companion object {
        private const val NEW_WORD_URGENCY = 5.0
        private const val LEARNING_STEP_DAYS = 5.0 / (24.0 * 60.0) // 5 minutes expressed in days
        private const val MILLIS_PER_DAY = 24.0 * 60.0 * 60.0 * 1000.0
        private const val DEFAULT_DAYS_SINCE = 14.0
        const val MIN_EASE = 1.3
        const val DEFAULT_EASE = 2.5
        const val MAX_EASE = 3.3
        private const val EASY_BONUS = 1.3

        private fun graduatedInterval(streak: Int, ease: Double, prevInterval: Double): Double =
            when (streak) {
                1 -> 1.0
                2 -> 6.0
                else -> (prevInterval * ease).coerceAtLeast(6.0)
            }
    }
}

enum class Difficulty(val label: String) {
    NEW("New"),
    EASY("Easy"),
    MEDIUM("Medium"),
    HARD("Hard"),
}
